import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SharedStateContext from "../../../SharedStateProvider";
import WebsiteTargetTable from "./WebsiteTargetTable/WebsiteTargetTable";
import WebsiteTargetTableHeading from "./WebsiteTargetTable/WebsiteTargetTableHeading";
import { websiteTargetEndPointLogic } from "../../../../logic/AssetManagement/WebsiteTarget/websiteTargetEndPointLogic";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserData,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { format } from "date-fns";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../Page404/Page403";
import { useLocation, useSearchParams } from "react-router-dom";
import store from "../../../../store";
import {
  setSelectedSharedUid,
  setSelectedSite,
  setSiteOptions,
  setSelectedDataSet,
} from "../../../../features/websiteTargetSlice";
import SimpleBackdrop from "../../../BackDrop/BackDrop";

const tableHeadings = {
  period: "Period",
  spend: "Spend",
  clicks: "Clicks",
  ftd: "FTD",
  cvr: "CVR",
  cac: "CAC",
  profit: "Profit",
  nrpu: "NRPU",
  cpa: "CPA",
  rs: "RS",
  total_income: "Total Income",
  cpa_pv: "CPA PV",
  rs_pv: "RS PV",
  pv: "PV",
  last_modified: "Last Modified",
  modified_by: "Modified By",
  more_info: "More Info",
};

const WebsiteTarget = ({ userToken }: any) => {
  const oauthTokenHeader = userToken;
  const response = useSelector(selectUserData);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [tableRefresh, setTableRefresh] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSharedUid = useSelector(
    (state: any) => state.websiteTarget.selectedSharedUid
  );
  const selectedSite = useSelector(
    (state: any) => state.websiteTarget.selectedSite
  );
  const siteOptions = useSelector(
    (state: any) => state.websiteTarget.siteOptions
  );
  const dispatch = useDispatch();
  const location = useLocation();

  const hasViewWebsiteTargetPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "view_website_target"
  );
  const hasCreateWebsiteTargetPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "create_website_target"
  );
  const hasEditWebsiteTargetPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "edit_website_target"
  );

  // Reset state only when component unmounts, not on mount
  useEffect(() => {
    return () => {
      // Reset when component unmounts (navigating away)
      store.dispatch(setSelectedSharedUid(null));
      store.dispatch(setSelectedSite(null));
    };
  }, []);

  useEffect(() => {
    if (response?.data?.website_permissions) {
      const siteOptions = response.data.website_permissions.map(
        (siteObj: any) => {
          const uid = Object.keys(siteObj)[0]; // UID of the site
          const siteName = siteObj[uid].ui_site_name; // Site name
          return { uid, siteName }; // Return as an object with uid and siteName
        }
      );
      store.dispatch(setSiteOptions(siteOptions)); // Dispatch the memoized options
    }
  }, [response]);

  useEffect(() => {
    if (!selectedSharedUid) {
      setSearch(false);
      setDataShow(false);
    } else if (selectedSite) {
      handleSearch();
    }
    setLoad(false);
    // eslint-disable-next-line
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite");
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("websiteTargetSharedUid");

    // Check if we have a site in the URL and siteOptions are loaded
    if (siteFromURL && siteOptions?.length > 0) {
      const isValidSite = siteOptions?.find(
        (site: any) => site.uid === siteFromURL
      );

      if (isValidSite) {
        store.dispatch(setSelectedSharedUid(siteFromURL)); // This is likely a page refresh, so restore the selected site
        store.dispatch(setSelectedSite(siteFromURL));
        store.dispatch(setSelectedDataSet("Key Metrics"));
        setSearch(true);
        setDataShow(true);
        updatedParams.set("data-set", "Key Metrics");
      } else {
        updatedParams.delete("selectedSite"); // Invalid site in URL, remove it
      }
    }
    setSearchParams(updatedParams, { replace: true });
    // eslint-disable-next-line
  }, [siteOptions]);

  // below portion is for setting and deleting the site
  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get existing params
    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the value
    } else {
      updatedParams.delete("selectedSite"); // Remove key if null
    }
    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading
  };

  const handleSearch = () => {
    if (selectedSharedUid) {
      setSearch(true);
      setDataShow(true);
      store.dispatch(setSelectedSite(selectedSharedUid));
      store.dispatch(setSelectedDataSet("Key Metrics"));
      const updatedParams = new URLSearchParams(window.location.search);
      updatedParams.set("selectedSite", selectedSharedUid);
      updatedParams.set("data-set", "Key Metrics");
      setSearchParams(updatedParams, { replace: true });
    }
  };

  const handleSelectWebsite = (_: unknown, newValue: any) => {
    const params = new URLSearchParams(location.search);

    if (!newValue) {
      store.dispatch(setSelectedSharedUid(null));
      setDataShow(false);
      updateURLParams(null); // Remove selectedSite from URL
    } else {
      store.dispatch(setSelectedSharedUid(newValue.uid));
      setSearch(false);
      store.dispatch(setSelectedSite(null));
      setDataShow(false); // Reset dataShow when a new site is selected
      params.delete("search");
      params.delete("filters");
      params.delete("page");
      setSearchParams(params);
    }
  };

  useEffect(() => {
    setDataLoaded(false);
    setTableRefresh(false);
    if (search === true && dataShow && selectedSharedUid?.length !== 0) {
      setLoading(true);
      const fetchTargetData = async () => {
        try {
          await websiteTargetEndPointLogic.getAllWebsiteTarget(
            oauthTokenHeader,
            selectedSharedUid,
            dispatch
          );
          await websiteTargetEndPointLogic.getWebsiteMicroTargets(
            oauthTokenHeader,
            selectedSharedUid,
            dispatch
          );
        } catch (error: any) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message:
                error?.parsedText ||
                "Error while fetching data. Please try again later.",
              type: "error",
            })
          );
        } finally {
          setLoading(false);
          setDataLoaded(true);
        }
      };
      fetchTargetData();
    }
    //eslint-disable-next-line
  }, [dataShow, selectedSharedUid, tableRefresh === true]);

  const websiteTargetList = useSelector(
    (state: any) => state.websiteTarget.websiteTargetList
  );
  const websiteMicroTargetList = useSelector(
    (state: any) => state.websiteTarget.microTargetList
  );

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return isNaN(date.getTime()) ? "N/A" : format(date, "MM/yyyy");
  };

  const formattedWebsiteTarget = websiteTargetList.map((target: any) => ({
    ...target,
    month: formatMonth(target?.month),
  }));

  const periods = formattedWebsiteTarget.map((target: any) => target?.month);

  return (
    <>
      {hasViewWebsiteTargetPermission ? (
        <>
          <Paper sx={{ padding: "8px 16px", mb: 2 }}>
            <Typography fontSize={24} sx={{ paddingBottom: "15px" }}>
              Website Projection
            </Typography>
            <Grid container mt={0} columnSpacing={3} mb={1}>
              <Grid item xs={10}>
                <Autocomplete
                  sx={{ width: "100%" }}
                  options={siteOptions}
                  getOptionLabel={(option: any) => option?.siteName}
                  value={
                    siteOptions.find(
                      (option: any) => option?.uid === selectedSharedUid
                    ) || null
                  }
                  onChange={handleSelectWebsite}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search site name or choose from the list"
                      label="Select Website"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  data-tracker="91"
                  variant="contained"
                  disabled={!selectedSharedUid}
                  onClick={handleSearch}
                  fullWidth
                  size="large"
                  color="secondary"
                  sx={{ py: "7px", px: "10px", whiteSpace: "nowrap" }}
                >
                  Select Site
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {dataShow && (
            <>
              {loading ? (
                <SimpleBackdrop />
              ) : dataLoaded ? (
                <>
                  <WebsiteTargetTableHeading
                    periods={periods}
                    selectedSharedUid={selectedSharedUid}
                    hasCreateWebsiteTargetPermission={
                      hasCreateWebsiteTargetPermission
                    }
                  />
                  <WebsiteTargetTable
                    tableHeadings={tableHeadings}
                    selectedSharedUid={selectedSharedUid}
                    siteOptions={siteOptions}
                    websiteTarget={websiteTargetList}
                    formattedWebsiteTarget={formattedWebsiteTarget}
                    websiteMicroTarget={websiteMicroTargetList}
                    hasEditWebsiteTargetPermission={
                      hasEditWebsiteTargetPermission
                    }
                  />
                </>
              ) : null}
            </>
          )}
        </>
      ) : (
        <Page404 />
      )}
    </>
  );
};

export default WebsiteTarget;
